/**
 * OrejaPanel - Main side panel for the Oreja voice-powered task update system.
 *
 * Renders as a right-side Sheet. Orchestrates session lifecycle by rendering
 * the appropriate phase component (recording, progress, clarification, review,
 * commit summary) based on the active session's current phase.
 *
 * @module OrejaPanel
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Clock, AudioLines, RotateCcw, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/shared/DialogWrapper';
import { useOrejaSessions } from '@/hooks/useOrejaSessions';
import { useOrejaSession, type OrejaInput } from '@/hooks/useOrejaSession';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { useClients } from '@/hooks/useClients';
import { ATTENTION_PHASES, PROCESSING_PHASES } from '@/lib/oreja/types';
import type { OrejaProjectOption } from './OrejaRecordingPhase';
import { OrejaPanelBody } from './OrejaPanelBody';
import { OrejaSessionHistory } from './OrejaSessionHistory';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrejaPanelProps {
  /** Whether the panel is open. */
  open: boolean;
  /** Callback when open state changes. */
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Main Oreja side panel rendered as a Sheet.
 *
 * @param props - Panel open state and change handler
 * @returns The Oreja panel Sheet component
 */
export function OrejaPanel({ open, onOpenChange }: OrejaPanelProps) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { clients: clientsData } = useClients();

  const [showHistory, setShowHistory] = useState(false);
  const [selectedProject, setSelectedProject] = useState<OrejaProjectOption | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const sessionManager = useOrejaSessions();

  const sessionHook = useOrejaSession({
    organizationId: organization?.id ?? '',
    userId: user?.id ?? '',
  });

  // Build project options from clients with ClickUp list IDs
  const projects = useMemo<OrejaProjectOption[]>(() => {
    if (!clientsData) return [];
    return clientsData
      .filter((c) => c.clickup_list_id && !c.is_archived)
      .map((c) => ({
        clientId: c.id,
        projectId: c.id,
        listId: c.clickup_list_id!,
        label: c.name,
      }));
  }, [clientsData]);

  // Build a client ID → name lookup from loaded clients data
  const clientNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (clientsData) {
      for (const c of clientsData) {
        map.set(c.id, c.name);
      }
    }
    return map;
  }, [clientsData]);

  // Build history entries for the sidebar, resolving client names
  const historyEntries = useMemo(
    () =>
      sessionManager.history.map((s) => ({
        id: s.id,
        client_id: s.client_id,
        client_name: s.client_id ? (clientNameMap.get(s.client_id) ?? null) : null,
        phase: s.phase,
        created_at: s.created_at,
        proposals: s.proposals,
        committed_changes: s.committed_changes,
      })),
    [sessionManager.history, clientNameMap],
  );

  const handleProjectSelect = useCallback(
    (clientId: string, projectId: string, listId: string) => {
      const project = projects.find(
        (p) => p.clientId === clientId && p.projectId === projectId && p.listId === listId,
      );
      setSelectedProject(project ?? null);
    },
    [projects],
  );

  const handleSend = useCallback(
    (input: OrejaInput) => {
      if (!selectedProject) return;
      const sessionId = sessionManager.startSession({
        clientId: selectedProject.clientId,
        projectId: selectedProject.projectId,
        clickupListId: selectedProject.listId,
      });
      void sessionHook.start({
        clientId: selectedProject.clientId,
        projectId: selectedProject.projectId,
        clickupListId: selectedProject.listId,
        clientName: selectedProject.label,
        projectName: selectedProject.label,
        userName: user?.user_metadata?.full_name || '',
        input,
      });
      sessionManager.updateSessionPhase(sessionId, 'transcribing');
    },
    [selectedProject, sessionManager, sessionHook],
  );

  const handleSendAnswers = useCallback(
    (input: OrejaInput, radioContext?: string) => {
      void sessionHook.sendAnswers(input, radioContext);
    },
    [sessionHook.sendAnswers],
  );

  const handleRefine = useCallback(() => {
    sessionHook.startRefine();
  }, [sessionHook]);

  const handleCommit = useCallback(
    (selectedUpdated: number[], selectedNew: number[]) => {
      void sessionHook.commit(selectedUpdated, selectedNew);
    },
    [sessionHook],
  );

  const handleStartNew = useCallback(() => {
    sessionHook.reset();
    setSelectedProject(null);
  }, [sessionHook]);

  const phase = sessionHook.phase;
  const hasUnsavedWork = phase !== 'recording' && phase !== 'committed';
  const sessionEntries = Array.from(sessionManager.sessions.values());

  /** Intercepts panel close to guard against unsaved work. */
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && hasUnsavedWork) {
        setConfirmClose(true);
        return;
      }
      onOpenChange(nextOpen);
    },
    [hasUnsavedWork, onOpenChange],
  );

  // Toast notification on successful commit
  const prevPhaseRef = useRef(phase);
  useEffect(() => {
    if (prevPhaseRef.current !== 'committed' && phase === 'committed') {
      toast.success('Changes pushed to ClickUp');
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] flex flex-col p-0">
        <SheetHeader className="px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <SheetTitle className="flex items-center gap-2">
                <AudioLines className="h-5 w-5 shrink-0" />
                Oreja
              </SheetTitle>
              <SheetDescription className="sr-only">
                Voice-powered task update panel
              </SheetDescription>
            </div>

            {/* Session tabs (when multiple) */}
            {sessionEntries.length > 1 && (
              <div className="flex gap-1">
                {sessionEntries.map((entry) => {
                  const isActive = entry.sessionId === sessionManager.activeSessionId;
                  const needsAttention = ATTENTION_PHASES.includes(entry.phase);
                  const isProc = PROCESSING_PHASES.includes(entry.phase);
                  return (
                    <button
                      key={entry.sessionId}
                      type="button"
                      onClick={() => sessionManager.switchSession(entry.sessionId)}
                      className={cn(
                        'px-2 py-1 text-xs rounded-md transition-colors',
                        isActive ? 'bg-accent font-medium' : 'hover:bg-muted',
                      )}
                    >
                      <span className="flex items-center gap-1">
                        <span
                          className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            needsAttention && 'bg-emerald-500',
                            isProc && 'bg-amber-500',
                            !needsAttention && !isProc && 'bg-muted-foreground',
                          )}
                        />
                        {entry.sessionId.slice(0, 4)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => setShowHistory(!showHistory)}
              aria-label="Toggle session history"
            >
              <Clock className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-hidden">
          {showHistory ? (
            <OrejaSessionHistory
              sessions={historyEntries}
              activeSessionId={sessionManager.activeSessionId}
              onSelectSession={(id) => {
                sessionManager.switchSession(id);
                setShowHistory(false);
              }}
              onClose={() => setShowHistory(false)}
              isLoading={sessionManager.isLoadingHistory}
            />
          ) : (
            <ScrollArea className="h-full">
              <OrejaPanelBody
                phase={phase}
                sessionHook={sessionHook}
                projects={projects}
                selectedProject={selectedProject}
                onProjectSelect={handleProjectSelect}
                onSend={handleSend}
                onSendAnswers={handleSendAnswers}
                onRefine={handleRefine}
                onCommit={handleCommit}
                onStartNew={handleStartNew}
              />
            </ScrollArea>
          )}
        </div>

        {/* Error display with retry */}
        {sessionHook.error && (
          <div className="border-t bg-destructive/15 px-4 py-3 text-sm">
            <div className="flex items-start gap-2">
              <span className="shrink-0 mt-0.5 h-4 w-4 rounded-full bg-destructive flex items-center justify-center">
                <span className="text-[10px] font-bold text-destructive-foreground">!</span>
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-destructive">Something went wrong</p>
                <p className="text-destructive/80 mt-0.5">{sessionHook.error}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {sessionHook.canRetry && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => void sessionHook.retry()}
                    disabled={sessionHook.isProcessing}
                    aria-label="Retry"
                  >
                    <RefreshCw className="mr-1.5 h-3 w-3" />
                    Retry
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleStartNew}
                  aria-label="Start over"
                >
                  <RotateCcw className="mr-1.5 h-3 w-3" />
                  Start Over
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>

      <ConfirmDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        title="Discard unsaved work?"
        description="You have an active Oreja session. Your session data is saved, but any unsaved inline edits will be lost."
        confirmLabel="Close anyway"
        cancelLabel="Keep working"
        variant="destructive"
        onConfirm={() => {
          setConfirmClose(false);
          onOpenChange(false);
        }}
      />
    </Sheet>
  );
}
